import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Camera, X, Loader2, Check, SwitchCamera, Zap, ZapOff, Mic, MapPin, MapPinOff, Satellite, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { queueItem, updateQueued, removeQueued, isQuotaError, rescueQueueItem, reportQueueError } from "@/lib/offlineQueue";
import { prepareImageForUpload, originalsWifiOnly, isOnWifi, highResPhotos } from "@/lib/imagePrep";
import { setCaptureActive } from "@/lib/appUpdate";
import { api } from "@/lib/api";
import { wtAddChunk, wtSaveSession, wtPendingChunks, wtMarkChunkUploaded, wtClearSession, flushWalkthroughs } from "@/lib/walkthroughStore";

// Group C4: one-tap area chips (stored in English so the AI report stays EN;
// labels render localized). "Other…" opens the free-text box.
const WT_AREAS = [
  { key: "kitchen", en: "Kitchen" }, { key: "bath", en: "Bath" }, { key: "bedroom", en: "Bedroom" },
  { key: "living", en: "Living" }, { key: "basement", en: "Basement" }, { key: "exterior", en: "Exterior" },
  { key: "roof", en: "Roof" }, { key: "deck", en: "Deck" }, { key: "garage", en: "Garage" },
];
import { deviceTz, freshEnough } from "@/lib/captureProof";
import { toast } from "sonner";
import CapturePreview from "@/components/CapturePreview";
import { useModalBack } from "@/lib/modalBack";
import VoiceRecorder from "@/components/VoiceRecorder";

const NAV_SPACE = "calc(72px + env(safe-area-inset-bottom, 0px))";

// Rapid Camera: live viewfinder (bottom nav stays visible), burst capture,
// zoom/flash/switch/focus via track capabilities, fixed control bar above nav.
// PROOF: a high-accuracy GPS watch runs the whole session; every shutter press
// snapshots the freshest fix (≤60s old, never cached) + its own timestamp, so
// each photo carries true capture-time location/time — even through offline
// queuing (stable client_id → server dedup → retries never duplicate).
export default function CameraCapture({ projectId, projectName = "", open, onClose, onUploaded, onFinish, initialMode = "photo" }) {
  useModalBack(open, onClose); // phone Back closes the camera (Round 10 #1)
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const fallbackRef = useRef(null);
  const pinchRef = useRef(null);
  const [shots, setShots] = useState([]); // {blob, url, cid, ts, tsMs, tz, fix}
  const [uploading, setUploading] = useState(false);
  const [errKind, setErrKind] = useState(null); // "denied" | "error" | null
  const [facing, setFacing] = useState("environment");
  const [zoomCaps, setZoomCaps] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [zoomFlash, setZoomFlash] = useState("");      // temporary "2.3x" label while pinching
  const [zoomFlashOn, setZoomFlashOn] = useState(false);
  const zoomFlashTimers = useRef([]);
  const [torchOn, setTorchOn] = useState(false);
  const [torchOk, setTorchOk] = useState(false);
  const [focusOk, setFocusOk] = useState(false);
  const [focusPt, setFocusPt] = useState(null);
  const [flashPulse, setFlashPulse] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [cat, setCat] = useState("progress");        // Before/Progress/After/Issue chip
  const [note, setNote] = useState("");               // optional voice note (transcribed)
  const [noteOpen, setNoteOpen] = useState(false);
  const noteSwipe = useRef(null);
  // Note sheet: never a dead end — X / tap outside / swipe down / Back / Esc all
  // close it WITHOUT saving; the Done check keeps the note and closes.
  const closeNoteDiscard = () => { setNote(""); setNoteOpen(false); };
  useModalBack(noteOpen, closeNoteDiscard);
  useEffect(() => {
    if (!noteOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeNoteDiscard(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [noteOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const watchIdRef = useRef(null);
  const latestFixRef = useRef(null);   // freshest GPS fix while the camera is open
  const locErrRef = useRef(null);      // denied | timeout | unavailable
  const [fixUi, setFixUi] = useState(null);   // state mirror for the status chip
  const [locErrUi, setLocErrUi] = useState(null);

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((tr) => tr.stop());
    streamRef.current = null;
    trackRef.current = null;
  };

  const startLocWatch = useCallback(() => {
    latestFixRef.current = null;
    locErrRef.current = null;
    setFixUi(null);
    setLocErrUi(null);
    if (!navigator.geolocation) { locErrRef.current = "unavailable"; setLocErrUi("unavailable"); return; }
    // Ask permission up-front and keep a live high-accuracy watch so GPS is
    // warm at shutter time. maximumAge: 0 = a stale cached fix is never used.
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const fix = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, fix_ts: p.timestamp || Date.now() };
        latestFixRef.current = fix;
        locErrRef.current = null;
        setFixUi(fix);
        setLocErrUi(null);
      },
      (err) => {
        const kind = err?.code === 1 ? "denied" : err?.code === 2 ? "unavailable" : "timeout";
        locErrRef.current = kind;
        setLocErrUi(kind);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 }
    );
  }, []);

  const stopLocWatch = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchIdRef.current);
    watchIdRef.current = null;
  }, []);

  // ---- 360 Walkthrough mode (Sprint Item 7 Phase 1): continuous audio in
  // 10s chunks (IndexedDB first, resumable, idempotent by session+seq) while
  // the shutter keeps grabbing stills — each photo stores t = seconds from start.
  const [mode, setMode] = useState(initialMode === "walkthrough" ? "walkthrough" : "photo");
  const wtModeRef = useRef(initialMode === "walkthrough");
  const [wtSession, setWtSession] = useState(null);
  const [wtState, setWtState] = useState("idle"); // idle|recording|paused|interrupted|finishing
  const [wtElapsed, setWtElapsed] = useState(0);
  const [wtAreas, setWtAreas] = useState([]);
  const [wtAreaInput, setWtAreaInput] = useState("");
  const [wtAreaOpen, setWtAreaOpen] = useState(false);
  const [wtAreaCustom, setWtAreaCustom] = useState(false); // "Other…" free-text box (Group C4)
  const [wtNotice, setWtNotice] = useState(false);
  const [wtFinishError, setWtFinishError] = useState(false); // Round 18 #1: Done failed — offer Retry, keep everything
  const [wtCloseAsk, setWtCloseAsk] = useState(false);       // Round 18 #1: confirm before discarding a recording
  const wtRecRef = useRef(null);
  const wtSessionRef = useRef(null); // state mirror readable inside async capture closures
  const wtWritesRef = useRef([]); // in-flight chunk IDB writes — awaited before finish (no lost tail)
  const wtSeqRef = useRef(0);
  const wtStartRef = useRef(0);
  const wtPausedAccumRef = useRef(0);
  const wtPauseStartRef = useRef(0);
  const wtPausedRef = useRef(false);
  const wtPhotosRef = useRef([]); // {client_id, t, taken_at, lat, lng}
  const wtWarnedRef = useRef(false);
  const wtWakeRef = useRef(null);
  const wtTimerRef = useRef(null);
  const wtFinishingRef = useRef(false);
  const wtQueuedCidsRef = useRef(new Set()); // R18 #1: photos already handed to the queue (Retry must not duplicate) // re-entry guard immune to stale closures (A1/A2)

  const wtNow = () => {
    if (!wtStartRef.current) return 0;
    const pausedExtra = wtPausedRef.current ? Date.now() - wtPauseStartRef.current : 0;
    return Math.max(0, (Date.now() - wtStartRef.current - wtPausedAccumRef.current - pausedExtra) / 1000);
  };
  const wtFmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const wtResetForNewSession = () => {
    // Round 18 #1: every walkthrough start must be a FRESH session — never
    // reuse a finished one (old area chips / dead Done button came from this).
    setWtSession(null);
    wtSessionRef.current = null;
    setWtState("idle");
    setWtAreas([]);
    setWtElapsed(0);
    setWtFinishError(false);
    wtPhotosRef.current = [];
    wtSeqRef.current = 0;
    wtWritesRef.current = [];
    wtWarnedRef.current = false;
    wtFinishingRef.current = false;
    wtQueuedCidsRef.current = new Set();
    wtStartRef.current = 0;
    wtPausedAccumRef.current = 0;
    wtPausedRef.current = false;
  };

  const startWalkthrough = async () => {
    if (wtState !== "idle") return;
    try {
      const { data } = await api.post("/walkthroughs", {
        project_id: projectId, lang: i18n.language?.startsWith("es") ? "es" : "en" });
      setWtSession(data);
      wtSessionRef.current = data;
      // Persist the session immediately so orphaned chunks (e.g. a tail chunk
      // landing after Done) stay flushable on any later online pass.
      wtSaveSession({ session_id: data.id, project_id: projectId || "", status: "recording", finishPayload: null }).catch(() => {});
      wtPhotosRef.current = [];
      setWtAreas([]);
      wtSeqRef.current = 0;
      wtWarnedRef.current = false;
      wtFinishingRef.current = false;
      wtWritesRef.current = [];
      const stream = streamRef.current;
      const audioTracks = stream?.getAudioTracks?.() || [];
      if (!audioTracks.length) { toast.error(t("u360_mic_blocked_type")); return; }
      const audioStream = new MediaStream([audioTracks[0]]); // audio track only — tiny chunks
      const MR = window.MediaRecorder;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((x) => MR?.isTypeSupported?.(x)) || "";
      const rec = mime ? new MR(audioStream, { mimeType: mime }) : new MR(audioStream);
      rec.ondataavailable = (e) => {
        if (!e.data?.size) return;
        const seq = wtSeqRef.current++;
        wtWritesRef.current.push(wtAddChunk(data.id, seq, e.data).catch(() => toast.error(t("queue_write_failed"))));
        if (navigator.onLine) {
          const fd = new FormData();
          fd.append("seq", String(seq));
          fd.append("file", e.data, `chunk-${seq}.webm`);
          wtWritesRef.current.push(
            api.post(`/walkthroughs/${data.id}/chunk`, fd,
              { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })
              .then(() => wtPendingChunks(data.id).then((cs) =>
                cs.filter((c) => c.seq === seq).forEach((c) => wtMarkChunkUploaded(c.cid))))
              .catch(() => {})); // stays in IndexedDB; the reconnect flush resends it
        }
      };
      wtRecRef.current = rec;
      rec.start(10000); // 10s chunks — a crash loses at most the current chunk
      wtStartRef.current = Date.now();
      wtPausedAccumRef.current = 0;
      wtPausedRef.current = false;
      setWtState("recording");
      setWtElapsed(0);
      wtTimerRef.current = setInterval(() => setWtElapsed(wtNow()), 1000);
      try { wtWakeRef.current = await navigator.wakeLock?.request("screen"); } catch { /* unsupported */ }
    } catch (e) { toast.error(t("bg_error")); }
  };

  const wtPause = (interrupted = false) => {
    try { wtRecRef.current?.pause(); } catch { /* ignore */ }
    if (!wtPausedRef.current) { wtPausedRef.current = true; wtPauseStartRef.current = Date.now(); }
    setWtState(interrupted ? "interrupted" : "paused");
  };
  const wtResume = async () => {
    try { wtRecRef.current?.resume(); } catch { /* ignore */ }
    if (wtPausedRef.current) {
      wtPausedAccumRef.current += Date.now() - wtPauseStartRef.current;
      wtPausedRef.current = false;
    }
    setWtState("recording");
    try { wtWakeRef.current = await navigator.wakeLock?.request("screen"); } catch { /* ignore */ }
  };

  // iOS Safari PWA stops recording on lock/background — pause gracefully and
  // KEEP everything recorded so far; on return the banner offers Resume.
  useEffect(() => {
    if (wtState !== "recording") return undefined;
    const onVis = () => {
      if (document.visibilityState === "hidden") wtPause(true);
    };
    const onHide = () => wtPause(true);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onHide);
    };
  }, [wtState]); // eslint-disable-line react-hooks/exhaustive-deps

  // 15-minute limit, warning at 13:00.
  useEffect(() => {
    if (wtState !== "recording") return;
    if (wtElapsed >= 780 && !wtWarnedRef.current) { wtWarnedRef.current = true; toast(t("wt_limit_warn")); }
    if (wtElapsed >= 900) finishWalkthrough();
  }, [wtElapsed, wtState]); // eslint-disable-line react-hooks/exhaustive-deps

  const wtAddArea = (labelOverride = "") => {
    const label = (labelOverride || wtAreaInput).trim();
    if (!label) return;
    setWtAreas((a) => [...a, { t: wtNow(), label }]);
    setWtAreaInput("");
    setWtAreaOpen(false);
    setWtAreaCustom(false);
  };

  // Fast Photos (A/B/C): compress on-device and hand to the background queue
  // AT SHUTTER TIME — the photo appears in the job grid instantly (from the
  // local blob) and uploads while the camera is still open. Done never waits.
  // Never throws: failures toast and leave shot.qid unset so Done can retry.
  // The full proof payload for a shot — shared by the capture-time queue
  // write and the Done-time rescue path (same client_id → dedupe-safe).
  const buildShotPayload = (shot, noteTxt = null) => {
    const fix = !shot.fromLibrary && shot.fix && freshEnough(shot.fix, shot.tsMs) ? shot.fix : null;
    const wt = wtModeRef.current;
    return {
      kind: "photo", projectId: projectId || "",
      fileBlob: shot.blob, filename: `capture-${shot.cid}.jpg`, type: "image/jpeg",
      client_id: shot.cid, // stable id → server dedup → retries never duplicate
      preparing: true,
      walkthrough_id: wt ? (wtSessionRef.current?.id || "") : "",
      wt_t: wt ? (shot.wt_t ?? null) : null,
      lat: fix?.lat ?? null, lng: fix?.lng ?? null,
      accuracy_m: fix?.accuracy != null ? Math.round(fix.accuracy) : null,
      device_capture_ts: shot.ts, device_tz: shot.tz || deviceTz(),
      location_source: fix ? "device-gps" : (locErrRef.current || "unavailable"),
      source: wt ? "walkthrough" : (shot.fromLibrary ? "upload" : "camera"),
      category: shot.cat || cat, note: (noteTxt ?? note).trim(), offline_created: true,
    };
  };

  const queueShotNow = (shot) => {
    // P0-1: queuedPromise resolves at the IndexedDB COMMIT of the raw blob —
    // from that moment the photo survives an app kill. The resize/upload
    // continue in the background; Done only waits on queuedPromise.
    let commitResolve;
    shot.queuedPromise = new Promise((res) => { commitResolve = res; });
    const p = (async () => {
      const fix = !shot.fromLibrary && shot.fix && freshEnough(shot.fix, shot.tsMs) ? shot.fix : null;
      // Instant tile FIRST (A): queue the on-device blob right away so grids
      // show it in <0.5s; the flush skips items still being prepared...
      const qid = await queueItem(buildShotPayload(shot));
      shot.qid = qid;
      commitResolve(qid); // durable from here — the crash-safe point
      if (wtModeRef.current && wtSessionRef.current?.id) wtQueuedCidsRef.current.add(shot.cid);
      // ...then resize in the background and swap the queued blob (C). The
      // 1.9s phone-side resize must never delay the tile.
      const prepared = await prepareImageForUpload(shot.blob).catch(() => null);
      if (!prepared) { await updateQueued(qid, { preparing: false }).catch(() => {}); return; }
      const meta = prepared.meta || {};
      let fileBlob = prepared.blob;
      if (originalsWifiOnly() && prepared.compressed) {
        if (isOnWifi()) fileBlob = shot.blob;
        else await queueItem({ kind: "original", client_id: shot.cid, fileBlob: shot.blob, filename: `original-${shot.cid}.jpg` });
      }
      await updateQueued(qid, {
        fileBlob, type: "image/jpeg", preparing: false,
        // Library EXIF wins over the live watch fix for time/place.
        lat: fix?.lat ?? meta.lat ?? null,
        lng: fix?.lng ?? meta.lng ?? null,
        location_source: fix ? "device-gps" : (meta.lat != null ? "exif" : (locErrRef.current || "unavailable")),
        device_capture_ts: (shot.fromLibrary && meta.taken) ? meta.taken : shot.ts,
      }).catch(() => updateQueued(qid, { preparing: false }).catch(() => {}));
    })().catch((e) => {
      commitResolve(null);
      console.warn("queue_write_failed", e);
      toast.error(isQuotaError(e) ? t("queue_storage_full") : t("queue_write_failed"));
    });
    return p;
  };

  // H: mode chips inside the camera — Library and Video need no menu first.
  const pickLibrary = () => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*";
    inp.multiple = true;
    inp.onchange = () => {
      for (const f of Array.from(inp.files || [])) {
        const shot = makeShot(f);
        shot.fromLibrary = true; // EXIF time/GPS wins over the live watch fix
        shot.queuePromise = queueShotNow(shot);
        setShots((s) => [...s, shot]);
      }
    };
    inp.click();
  };

  const pickVideo = () => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "video/*";
    inp.onchange = () => {
      const f = inp.files?.[0];
      if (!f) return;
      void queueItem({
        kind: "photo", projectId: projectId || "", fileBlob: f, filename: f.name || `video-${Date.now()}.mp4`,
        type: f.type || "video/mp4", client_id: crypto.randomUUID(),
        device_capture_ts: new Date().toISOString(), device_tz: deviceTz(), location_source: "",
        source: "upload", offline_created: true,
      }).then(() => toast.success(t("cam_saved", { count: 1 }), { id: "cam-saved", duration: 3000 }))
        .catch((e) => toast.error(isQuotaError(e) ? t("queue_storage_full") : t("queue_write_failed")));
    };
    inp.click();
  };

  const addShot = (blob) => {
    const shot = makeShot(blob);
    if (wtModeRef.current) {
      shot.wt_t = wtNow();
      wtPhotosRef.current.push({ client_id: shot.cid, t: shot.wt_t, taken_at: shot.ts,
        lat: shot.fix?.lat ?? null, lng: shot.fix?.lng ?? null });
    }
    shot.queuePromise = queueShotNow(shot); // upload starts NOW, not at Done
    setShots((s) => [...s, shot]);
  };

  const finishWalkthrough = async () => {
    // REF guard, not state: our own trailing handleClose() runs with a stale
    // wtState closure — without this, finish re-enters forever, re-queueing
    // the same photos and re-posting finish in a loop (Group A1/A2).
    if (wtFinishingRef.current || wtState === "idle") return;
    wtFinishingRef.current = true;
    setWtState("finishing");
    clearInterval(wtTimerRef.current);
    try { wtWakeRef.current?.release(); } catch { /* ignore */ }
    const rec = wtRecRef.current;
    const sid = wtSession?.id;
    await new Promise((res) => {
      if (!rec || rec.state === "inactive") return res();
      rec.addEventListener("stop", res, { once: true });
      try { rec.stop(); } catch { res(); }
    });
    // The final chunk's IndexedDB write lands asynchronously around 'stop' —
    // give the trailing dataavailable a beat to fire, then wait for every
    // chunk write/upload to settle or the tail of the recording is silently
    // dropped from the stitched audio.
    await new Promise((r) => setTimeout(r, 400));
    await Promise.allSettled(wtWritesRef.current);
    // Await only the raw IDB commits so the session link can be patched on
    // anything that raced ahead of the walkthrough start (P0-4: never resizes).
    await Promise.allSettled(shots.map((s) => s.queuedPromise || s.queuePromise).filter(Boolean));
    // Photos go through the SAME offline-safe queue, tagged with the session + t.
    const pid = projectId || "";
    for (const shot of shots) {
      if (wtQueuedCidsRef.current.has(shot.cid)) continue; // R18 #1: Retry must not re-queue
      if (shot.qid != null) {
        // Queued at shutter time (before the session id existed or without
        // the link) — patch it instead of re-queueing (client_id dedupes).
        await updateQueued(shot.qid, { walkthrough_id: sid || "", wt_t: shot.wt_t ?? null,
          source: "walkthrough", note: note.trim() }).catch(() => {});
        wtQueuedCidsRef.current.add(shot.cid);
        continue;
      }
      const fix = shot.fix && freshEnough(shot.fix, shot.tsMs) ? shot.fix : null;
      try {
        await queueItem({ kind: "photo", projectId: pid, fileBlob: shot.blob,
          filename: `wt-${shot.cid}.jpg`, type: "image/jpeg",
          client_id: shot.cid, walkthrough_id: sid || "", wt_t: shot.wt_t ?? null,
          lat: fix?.lat ?? null, lng: fix?.lng ?? null,
          accuracy_m: fix?.accuracy != null ? Math.round(fix.accuracy) : null,
          device_capture_ts: shot.ts, device_tz: shot.tz || deviceTz(),
          location_source: fix ? "device-gps" : (locErrRef.current || "unavailable"),
          source: "walkthrough", category: shot.cat || "", note: note.trim(), offline_created: true });
        wtQueuedCidsRef.current.add(shot.cid);
      } catch (e) { toast.error(isQuotaError(e) ? t("queue_storage_full") : t("queue_write_failed")); }
    }
    const finishPayload = { photos: wtPhotosRef.current, areas: wtAreas, duration_s: Math.round(wtNow()) };
    // P0-4: Done closes INSTANTLY. The session (audio chunks + payload) is
    // persisted to IndexedDB FIRST, then the build runs in the background —
    // the pill shows "Building walkthrough…" → "Walkthrough ready ✓".
    await wtSaveSession({ session_id: sid, project_id: pid, status: "pending_finish", finishPayload }).catch(() => {});
    toast.success(t("wt_building"), { id: "wt-build", duration: 3000 });
    try { window.dispatchEvent(new CustomEvent("bf:wt-building", { detail: { sessionId: sid, projectId: pid } })); } catch { /* non-browser env */ }
    handleClose();
    flushWalkthroughs(api).catch(() => {}); // uploads tail chunks → posts finish → fires bf:walkthrough-ready
  };

  const retryWtFinish = () => {
    setWtFinishError(false);
    finishWalkthrough();
  };

  const discardWalkthrough = async () => {
    const sid = wtSession?.id;
    try { wtRecRef.current?.stop(); } catch { /* ignore */ }
    clearInterval(wtTimerRef.current);
    try { wtWakeRef.current?.release(); } catch { /* ignore */ }
    if (sid) { try { await wtClearSession(sid); } catch { /* ignore */ } }
    setWtCloseAsk(false);
    wtResetForNewSession();
    handleClose(true);
  };

  const switchMode = async (m) => {
    if (m === "video") { pickVideo(); return; }   // H: native camcorder picker
    if (m === "library") { pickLibrary(); return; } // H: library chip, no menu first
    if (m === "walkthrough" && wtModeRef.current) return;
    if (m !== "walkthrough" && !wtModeRef.current) return;
    if (m !== "walkthrough" && wtState !== "idle") return; // finish or Back out first
    if (m === "walkthrough") {
      wtModeRef.current = true;
      setMode("walkthrough");
      await start("environment"); // restart the stream WITH the mic (one permission)
      if (!localStorage.getItem("bf.wt.notice")) { setWtNotice(true); return; }
      await startWalkthrough();
    } else {
      wtModeRef.current = false;
      setMode("photo");
      await start("environment");
    }
  };

  const startInflightRef = useRef(null); // serialize getUserMedia — concurrent calls hang iOS/headless
  const facingInitRef = useRef(true);
  const start = useCallback(async (which) => {
    if (startInflightRef.current) return startInflightRef.current;
    const p = (async () => {
      setErrKind(null);
      stopStream();
      try {
        // P0-2: memory-safe default stream — 1920x1440 (≈2.7MP frames, sharp,
        // <1MB). The 4K stream + takePhoto() stay behind the opt-in "High-res
        // photos" setting: full-sensor captures OOM-killed Android in the field.
        const hi = highResPhotos();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: hi
            ? { facingMode: { ideal: which }, width: { ideal: 3840 }, height: { ideal: 2160 } }
            : { facingMode: { ideal: which }, width: { ideal: 1920 }, height: { ideal: 1440 } },
          audio: wtModeRef.current,
        });
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const caps = track.getCapabilities?.() || {};
        setZoomCaps(caps.zoom ? { min: caps.zoom.min, max: caps.zoom.max } : null);
        const settings = track.getSettings?.() || {};
        setZoom(settings.zoom || caps.zoom?.min || 1);
        setTorchOk(!!caps.torch);
        setTorchOn(false);
        setFocusOk(!!caps.focusMode || "pointsOfInterest" in caps);
      } catch (e) {
        setErrKind(e?.name === "NotAllowedError" ? "denied" : "error");
      }
    })();
    startInflightRef.current = p;
    try { await p; } finally { startInflightRef.current = null; }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCaptureActive(true); // A4: suppress the Update banner / auto-reload while the camera is up
    setShots([]);
    setPreviewIdx(null);
    setFacing("environment");
    // Round 18 #1: every camera open starts from a CLEAN walkthrough slate —
    // a finished session's id/areas/timer must never bleed into the next one.
    wtResetForNewSession();
    // P0-3: Camera ALWAYS opens in Photo mode — a plain photo session must
    // never silently become a walkthrough (mic on, walkthrough build at Done).
    // Walkthrough starts only from the Walkthrough chip or an explicit
    // walkthrough open (360 panel). No last-used mode for Walkthrough.
    const wantMode = initialMode === "walkthrough" ? "walkthrough" : "photo";
    wtModeRef.current = wantMode === "walkthrough";
    setMode(wantMode);
    start("environment").then(() => {
      if (!wtModeRef.current) return;
      // Repeat users skip the explainer — recording starts right away.
      try { if (localStorage.getItem("bf.wt.notice")) { startWalkthrough(); return; } } catch { /* ignore */ }
      setWtNotice(true);
    });
    startLocWatch();
    return () => { setCaptureActive(false); stopStream(); stopLocWatch(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Skip the mount firing — the [open] effect already starts the camera;
    // this effect is only for flip-camera AFTER the first open.
    if (facingInitRef.current) { facingInitRef.current = false; return; }
    if (open) start(facing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  const flashZoom = (z) => {
    setZoomFlash(`${z.toFixed(1)}x`);
    setZoomFlashOn(true);
    zoomFlashTimers.current.forEach(clearTimeout);
    zoomFlashTimers.current = [
      setTimeout(() => setZoomFlashOn(false), 1000),
      setTimeout(() => setZoomFlash(""), 1400),
    ];
  };
  const applyZoom = useCallback(async (v) => {
    const track = trackRef.current;
    if (!track || !zoomCaps) return;
    const z = Math.min(zoomCaps.max, Math.max(zoomCaps.min, v));
    if (Math.abs(z - zoom) < 0.02) return; // skip micro-updates while pinching
    try {
      await track.applyConstraints({ advanced: [{ zoom: z }] });
      setZoom(z);
      flashZoom(z);
    } catch (e) { /* unsupported */ }
  }, [zoomCaps, zoom]);

  const pinchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinchRef.current = { d: pinchDist(e.touches), z: zoom };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current && zoomCaps) {
      applyZoom(pinchRef.current.z * (pinchDist(e.touches) / pinchRef.current.d));
    }
  };

  const onTapFocus = async (e) => {
    if (!focusOk || !trackRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    try {
      await trackRef.current.applyConstraints({ advanced: [{ pointsOfInterest: [{ x, y }] }] });
      setFocusPt({ x: x * 100, y: y * 100 });
      setTimeout(() => setFocusPt(null), 700);
    } catch (err) { /* ignore */ }
  };

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (e) { /* ignore */ }
  };

  // Every shot is stamped with its own client id, shutter time, timezone and
  // the freshest GPS fix available AT THAT MOMENT (≤60s old, else none).
  const makeShot = (blob) => {
    const nowMs = Date.now();
    const fix = latestFixRef.current;
    return {
      blob, url: URL.createObjectURL(blob), cid: crypto.randomUUID(), cat,
      ts: new Date(nowMs).toISOString(), tsMs: nowMs, tz: deviceTz(),
      fix: freshEnough(fix, nowMs) ? { ...fix } : null,
    };
  };

  const capture = async () => {
    setFlashPulse(true);
    setTimeout(() => setFlashPulse(false), 180);
    // P0-2: video-frame capture by default (memory-safe). takePhoto()'s
    // full-sensor stills are only used with the "High-res photos" setting ON.
    const track = trackRef.current;
    if (track && highResPhotos() && typeof window.ImageCapture === "function") {
      try {
        const blob = await new window.ImageCapture(track).takePhoto();
        if (blob) { addShot(blob); return; }
      } catch { /* fall back to the video frame */ }
    }
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (blob) addShot(blob);
    }, "image/jpeg", 0.92);
  };

  const onFallbackFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const bmp = await createImageBitmap(f, { imageOrientation: "from-image" });
      const c = document.createElement("canvas");
      c.width = bmp.width;
      c.height = bmp.height;
      c.getContext("2d").drawImage(bmp, 0, 0);
      c.toBlob((blob) => {
        if (blob) addShot(blob);
      }, "image/jpeg", 0.92);
    } catch (err) {
      addShot(f);
    }
  };

  const removeShot = (i) => setShots((s) => {
    if (s[i]) URL.revokeObjectURL(s[i].url);
    if (s[i]?.qid != null) removeQueued(s[i].qid).catch(() => {}); // discard cancels its upload
    if (wtModeRef.current && s[i]) wtPhotosRef.current = wtPhotosRef.current.filter((p) => p.client_id !== s[i].cid);
    return s.filter((_, idx) => idx !== i);
  });

  const keepShot = (i, blob, url) => setShots((s) =>
    s.map((sh, idx) => {
      if (idx !== i) return sh;
      URL.revokeObjectURL(sh.url);
      return { ...sh, blob, url }; // rotation bake preserves proof metadata
    })
  );

  const handleClose = (e) => {
    const force = e === true; // onClick passes a MouseEvent — never truthy-compare it
    // Round 18 #1: closing mid-recording must ASK first — never silently finish
    // or silently discard. Finish errors surface their own Retry banner instead.
    if (!force && wtFinishError) { setWtCloseAsk(true); return; }
    if (!force && wtModeRef.current && !wtFinishingRef.current && ["recording", "paused", "interrupted"].includes(wtState)) {
      setWtCloseAsk(true);
      return;
    }
    // Bulletproof: cleanup must never prevent the camera from closing.
    try { shots.forEach((s) => URL.revokeObjectURL(s.url)); } catch { /* ignore */ }
    try { stopLocWatch(); } catch { /* ignore */ }
    setShots([]);
    setPreviewIdx(null);
    onClose();
  };

  // DONE: shots were queued at shutter time (uploads already running in the
  // background) — Done only patches the shared voice note, retries any shot
  // whose queue write failed, and closes. It NEVER waits on network.
  const finish = async () => {
    if (!shots.length || uploading) return;
    setUploading(true);
    const pid = projectId || "";
    // Safety net: a visible project chip with an empty id means the picker state
    // desynced — NEVER silently file those photos into Unassigned.
    if (!pid && projectName) {
      setUploading(false);
      toast.error(t("cam_project_resync", "Project link lost — please re-pick the project"));
      window.dispatchEvent(new Event("bf:camera-change-project"));
      return;
    }
    const list = [...shots];
    const t0 = performance.now();
    const savedAtTap = list.filter((s) => s.qid != null).length;
    // STOP-SHIP final: Done NEVER waits on the store — not even a capped
    // race. Shots queue at shutter time; any straggler goes to the in-memory
    // rescue loop (client_id dedupe makes double-queueing impossible) and the
    // pill shows "Saving N photos…" until the write lands.
    const txt = note.trim();
    for (const shot of list) {
      if (shot.qid == null) {
        rescueQueueItem(buildShotPayload(shot, txt)); // capture-time save slow/failed — retries in background
      } else if (txt) {
        updateQueued(shot.qid, { note: txt }).catch(() => {});
      }
    }
    // B10: a shot already uploaded before Done never got the note — patch the
    // last shot by client_id (the server stashes it if the commit is still
    // mid-flight); a hard failure falls back to a job log. Never dropped.
    if (txt && list.length && navigator.onLine) {
      try {
        await api.post("/photos/late-note", { client_id: list[list.length - 1].cid, note: txt });
      } catch {
        await queueItem({ kind: "log", projectId: pid, text: txt, offline_created: true }).catch(() => {});
      }
    }
    // Field telemetry (James asked): if Done ever exceeds 300ms on a real
    // phone, the timings land in Admin > Upload health so we can see exactly
    // which step ate it — no more guessing.
    const doneMs = Math.round(performance.now() - t0);
    if (doneMs > 300) {
      reportQueueError(null, "done_timing", `done=${doneMs}ms shots=${list.length} saved_at_tap=${savedAtTap}`, null);
    }
    const n = list.length;
    try {
      list.forEach((s) => URL.revokeObjectURL(s.url));
      setShots([]);
      setPreviewIdx(null);
      toast.success(navigator.onLine
        ? (projectName ? t("cam_saved_to", { count: n, project: projectName }) : t("cam_saved", { count: n }))
        : t("offline_queued_count", { count: n }), { id: "cam-saved", duration: 3000 }); // newest replaces; auto-hide 3s
    } finally {
      // Done ALWAYS closes the camera and routes out — a cleanup/toast hiccup
      // must never leave the viewfinder stuck with a greyed button.
      setUploading(false);
      handleClose();
      onFinish?.(pid);
    }
  };

  if (!open) return null;
  const gpsChip = locErrUi === "denied"
    ? { cls: "bg-red-600/90 text-white", Icon: MapPinOff, label: t("cam_gps_denied") }
    : fixUi
      ? { cls: "bg-green-600/90 text-white", Icon: MapPin, label: t("cam_gps_ok", { m: Math.round(fixUi.accuracy || 0) }) }
      : locErrUi
        ? { cls: "bg-red-600/90 text-white", Icon: MapPinOff, label: t("cam_gps_none") }
        : { cls: "bg-slate-800/80 text-white", Icon: Satellite, label: t("cam_gps_search") };

  return createPortal(
    <>
      <input ref={fallbackRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFallbackFile} data-testid="cam-fallback-input" />
      <div
        className="fixed inset-0 z-[90] bg-black flex flex-col touch-none"
        data-testid="camera-capture"
      >
        <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent">
          <button onClick={handleClose} data-testid="cam-close" aria-label={t("cam_close")}
            className="rounded-full bg-slate-800/80 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
          <button onClick={() => window.dispatchEvent(new Event("bf:camera-change-project"))} data-testid="cam-project-chip"
            aria-label={t("cam_choose_project")}
            className="flex min-w-0 max-w-[46%] items-center gap-1 rounded-full bg-slate-800/80 px-3.5 text-[11px] font-bold text-white min-h-[36px] active:scale-95">
            <span className="truncate">{projectName || t("photos_unassigned")}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => setNoteOpen((v) => !v)} data-testid="cam-note-toggle" aria-label={t("cam_note")} aria-pressed={noteOpen || !!note}
              className={`flex items-center justify-center rounded-full p-3 min-h-[44px] min-w-[44px] ${note || noteOpen ? "bg-brand text-white" : "bg-slate-800/80 text-white"}`}>
              <Mic className="h-4 w-4" />
            </button>
            {torchOk && (
              <button onClick={toggleTorch} data-testid="cam-flash" aria-label={t("cam_flash")} aria-pressed={torchOn}
                className={`rounded-full p-3 min-h-[44px] min-w-[44px] flex items-center justify-center ${torchOn ? "bg-brand text-white" : "bg-slate-800/80 text-white"}`}>
                {torchOn ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}
              </button>
            )}
            <button onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} data-testid="cam-switch" aria-label={t("cam_switch")}
              className="rounded-full bg-slate-800/80 p-3 text-white min-h-[44px] min-w-[44px] flex items-center justify-center">
              <SwitchCamera className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!errKind && (
          <div className="absolute top-16 inset-x-0 z-10 flex flex-col items-center gap-2 pointer-events-none">
            <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-slate-900/70 p-1" data-testid="cam-mode-row">
              {["photo", "walkthrough", "video", "library"].map((m) => (
                <button key={m} onClick={() => switchMode(m)} data-testid={`cam-mode-${m}`} aria-pressed={mode === m}
                  className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold min-h-[36px] ${mode === m ? "bg-brand text-white" : "text-white/80"}`}>
                  {m === "walkthrough" && wtState === "recording" && <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />}
                  {m === "library" ? t("cam_mode_library") : t(`wt_mode_${m}`)}
                </button>
              ))}
            </div>
            {mode === "walkthrough" && wtState === "recording" && (
              <div data-testid="wt-recording-banner" role="alert"
                className="pointer-events-none absolute inset-x-4 top-[calc(max(env(safe-area-inset-top),12px)+126px)] z-30 flex items-center justify-center gap-2 rounded-2xl bg-red-600/95 px-4 py-3 text-base font-extrabold tracking-wide text-white shadow-xl">
                <span className="h-3 w-3 animate-pulse rounded-full bg-white" /> {t("wt_banner_recording")}
              </div>
            )}
            {mode === "walkthrough" && wtState !== "idle" && (
              <div className="pointer-events-auto flex flex-col items-center gap-1.5">
                <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold ${wtState === "recording" ? "bg-red-600/90 text-white" : "bg-amber-500/90 text-white"}`}
                  data-testid="wt-hud" role="status">
                  <span className={`h-2 w-2 rounded-full bg-white ${wtState === "recording" ? "animate-pulse" : ""}`} />
                  {wtFmt(wtElapsed)}
                </span>
                <span className="rounded-full bg-black/60 px-3 py-1 text-[10px] font-semibold text-white/90" data-testid="wt-hint">
                  {t("wt_hint")} · {t("wt_keep_open")}
                </span>
                {wtState === "interrupted" && (
                  <button onClick={wtResume} data-testid="wt-interrupted-banner"
                    className="rounded-full bg-amber-500 px-3.5 py-2 text-[11px] font-bold text-white active:scale-95">
                    {t("wt_interrupted")} {t("wt_resume")}
                  </button>
                )}
              </div>
            )}
            <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold ${gpsChip.cls}`}
              data-testid="cam-gps-status" data-state={fixUi ? "locked" : (locErrUi || "searching")} role="status">
              <gpsChip.Icon className="h-3.5 w-3.5" aria-hidden="true" /> {gpsChip.label}
            </span>
          </div>
        )}

        {mode === "walkthrough" && wtState !== "idle" && !errKind && (
          <div className="absolute bottom-[218px] inset-x-0 z-10 flex flex-wrap items-center justify-center gap-1.5 px-3" data-testid="wt-controls">
            <button onClick={() => (wtState === "recording" ? wtPause() : wtResume())} data-testid="wt-pause-resume"
              className="whitespace-nowrap rounded-full bg-black/60 border border-white/30 px-3 py-2 text-[11px] font-bold text-white min-h-[44px] active:scale-95">
              {wtState === "recording" ? t("wt_pause") : t("wt_resume")}
            </button>
            <button onClick={() => setWtAreaOpen((v) => !v)} data-testid="wt-new-area"
              className="whitespace-nowrap rounded-full bg-black/60 border border-white/30 px-3 py-2 text-[11px] font-bold text-white min-h-[44px] active:scale-95">
              {t("wt_new_area")}
            </button>
          </div>
        )}
        {wtAreaOpen && mode === "walkthrough" && (
          <div className="absolute bottom-[270px] inset-x-4 z-10 rounded-2xl bg-white p-2 shadow-xl" data-testid="wt-area-box">
            <div className="flex flex-wrap gap-1.5" data-testid="wt-area-chips">
              {WT_AREAS.map((a) => (
                <button key={a.key} onClick={() => wtAddArea(a.en)} data-testid={`wt-area-chip-${a.key}`}
                  className="rounded-full bg-slate-100 border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 min-h-[44px] active:scale-95">
                  {t(`wt_area_${a.key}`)}
                </button>
              ))}
              <button onClick={() => setWtAreaCustom((v) => !v)} data-testid="wt-area-chip-other"
                className="rounded-full bg-slate-100 border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 min-h-[44px] active:scale-95">
                {t("wt_area_other")}
              </button>
              <button onClick={() => setWtAreaOpen(false)} data-testid="wt-area-close" aria-label={t("action_cancel")}
                className="ml-auto flex h-11 w-11 items-center justify-center rounded-full text-slate-400 hover:text-slate-700 active:scale-95">
                <X className="h-4 w-4" />
              </button>
            </div>
            {wtAreaCustom && (
              <div className="mt-2 flex gap-2">
                <input value={wtAreaInput} onChange={(e) => setWtAreaInput(e.target.value)} placeholder={t("wt_area_ph")}
                  data-testid="wt-area-input" className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  onKeyDown={(e) => { if (e.key === "Enter") wtAddArea(); }} autoFocus />
                <button onClick={() => wtAddArea()} data-testid="wt-area-add"
                  className="rounded-xl bg-brand px-4 text-xs font-bold text-white min-h-[40px]">{t("wt_area_add")}</button>
              </div>
            )}
          </div>
        )}
        {wtAreas.length > 0 && mode === "walkthrough" && (
          <div className="absolute bottom-[270px] inset-x-0 z-[9] flex flex-wrap justify-center gap-1 px-4 pointer-events-none" data-testid="wt-areas-row">
            {wtAreas.map((a, i) => (
              <span key={i} className="rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold text-white/90">{a.label} · {wtFmt(a.t)}</span>
            ))}
          </div>
        )}

        {errKind ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cam-failed">
            <p className="text-slate-200 text-sm font-medium">
              {errKind === "denied" ? t("cam_permission_denied") : t("cam_start_failed")}
            </p>
            <button onClick={() => fallbackRef.current?.click()} data-testid="cam-use-phone"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-bold text-white min-h-[48px] active:scale-95">
              <Camera className="h-5 w-5" /> {t("cam_use_phone")}
            </button>
          </div>
        ) : (
          <div className="flex-1 relative overflow-hidden touch-none" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onClick={onTapFocus}>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 h-full w-full object-cover touch-none" data-testid="cam-viewfinder" />
            {focusPt && (
              <div className="absolute h-16 w-16 border-2 border-brand rounded-lg pointer-events-none"
                style={{ left: `${focusPt.x}%`, top: `${focusPt.y}%`, transform: "translate(-50%,-50%)" }} data-testid="cam-focus-ring" />
            )}
            {flashPulse && <div className="absolute inset-0 bg-white pointer-events-none" data-testid="cam-flash-pulse" />}
          </div>
        )}

        {!errKind && mode !== "walkthrough" && (
          <div className="absolute inset-x-0 z-10 flex flex-wrap items-center justify-center gap-1.5 px-3 bottom-[calc(max(env(safe-area-inset-bottom,0px),12px)+84px)]" data-testid="cam-category-row">
            {["before", "progress", "after", "issue"].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cam-cat-${c}`} aria-pressed={cat === c}
                className={`rounded-full px-3.5 py-2 text-xs font-bold min-h-[44px] ${cat === c ? "bg-brand text-white" : "bg-black/60 text-white border border-white/30"}`}>
                {t(`photo_${c}`)}
              </button>
            ))}
          </div>
        )}
        {noteOpen && !errKind && (
          <>
            <div className="absolute inset-0 z-[11] bg-black/40" data-testid="cam-note-backdrop" onClick={closeNoteDiscard} />
            <div className="absolute bottom-[218px] inset-x-4 z-[12] rounded-2xl bg-white p-2 shadow-xl" data-testid="cam-note-box"
              onTouchStart={(e) => { noteSwipe.current = e.touches[0].clientY; }}
              onTouchMove={(e) => {
                if (noteSwipe.current != null && e.touches[0].clientY - noteSwipe.current > 60) {
                  noteSwipe.current = null;
                  closeNoteDiscard();
                }
              }}>
              <div className="mb-1 flex items-center justify-between pl-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("cam_note")}</span>
                <span className="flex items-center gap-1">
                  <button onClick={() => setNoteOpen(false)} data-testid="cam-note-done" aria-label={t("cam_done")}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-brand text-white active:scale-95">
                    <Check className="h-5 w-5" />
                  </button>
                  <button onClick={closeNoteDiscard} data-testid="cam-note-cancel" aria-label={t("action_cancel")}
                    className="flex h-11 w-11 items-center justify-center rounded-full text-slate-400 hover:text-slate-700 active:scale-95">
                    <X className="h-5 w-5" />
                  </button>
                </span>
              </div>
              <VoiceRecorder value={note} onChange={setNote} />
            </div>
          </>
        )}

        {/* Pinch-to-zoom only (no preset buttons over the photo); a temporary
            "2.3x" label appears while pinching and fades out after 1s. */}
        {zoomFlash && (
          <div className="absolute inset-x-0 top-1/3 z-20 flex justify-center pointer-events-none" data-testid="cam-zoom-flash">
            <span className={`rounded-full bg-black/60 px-3 py-1.5 text-sm font-bold text-white transition-opacity duration-300 ${zoomFlashOn ? "opacity-100" : "opacity-0"}`}>{zoomFlash}</span>
          </div>
        )}
      </div>

      <div
        className="fixed inset-x-0 z-[91] flex items-center justify-between px-5 bottom-[max(env(safe-area-inset-bottom,0px),12px)] md:bottom-6"
        data-testid="cam-control-bar"
      >
        <button onClick={() => shots.length && setPreviewIdx(shots.length - 1)} disabled={!shots.length}
          data-testid="cam-last-shot" aria-label={t("cam_preview")}
          className="relative h-14 w-14 rounded-xl overflow-hidden border-2 border-white/70 bg-slate-800 min-h-[44px] min-w-[44px] disabled:opacity-60">
          {shots.length ? (
            <img src={shots[shots.length - 1].url} alt={t("cam_preview")} className="h-full w-full object-cover" />
          ) : (
            <Camera className="h-5 w-5 text-slate-500 mx-auto mt-4" />
          )}
          {shots.length > 0 && mode === "walkthrough" && shots[shots.length - 1].wt_t != null && (
            <span className="absolute -bottom-1.5 -left-1.5 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-black text-white" data-testid="cam-shot-t">
              📍 {wtFmt(shots[shots.length - 1].wt_t)}
            </span>
          )}
          {shots.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 rounded-full bg-brand text-white text-[10px] font-black px-1.5 py-0.5" data-testid="cam-count">
              {shots.length}
            </span>
          )}
        </button>
        <button onClick={capture} disabled={!!errKind} data-testid="cam-capture" aria-label={t("cam_shutter")}
          className="rounded-full border-4 border-white bg-brand flex items-center justify-center active:scale-90 transition-transform touch-manipulation disabled:opacity-40 bf-brand-shadow"
          style={{ height: 68, width: 68 }}>
          <Camera className="h-7 w-7 text-white" />
        </button>
        <button onClick={mode === "walkthrough" ? finishWalkthrough : finish}
          disabled={mode === "walkthrough" ? (wtState === "idle" || wtState === "finishing") : (uploading || shots.length === 0)}
          data-testid="cam-done"
          className="inline-flex items-center gap-2 rounded-full bg-white px-5 font-bold text-slate-900 active:scale-95 disabled:opacity-40 min-h-[48px]">
          {uploading || wtState === "finishing" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
          {t("cam_done")}
        </button>
      </div>

      {wtNotice && createPortal(
        <div className="fixed inset-0 z-[96] flex items-end justify-center bg-black/60 sm:items-center" data-testid="wt-notice" onClick={() => setWtNotice(false)}>
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))] sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-base font-extrabold text-slate-900">{t("wt_mode_walkthrough")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("wt_notice")}</p>
            <p className="mt-1 text-xs text-slate-400">{t("wt_keep_open")}</p>
            <button onClick={async () => {
              try { localStorage.setItem("bf.wt.notice", "1"); } catch { /* ignore */ }
              setWtNotice(false);
              await startWalkthrough();
            }} data-testid="wt-notice-ok"
              className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-brand text-sm font-bold text-white active:scale-95">
              {t("wt_notice_ok")}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Round 18 #1: closing mid-recording asks before discarding anything. */}
      {wtCloseAsk && createPortal(
        <div className="fixed inset-0 z-[96] flex items-end justify-center bg-black/60 sm:items-center" data-testid="wt-discard-dialog"
          role="alertdialog" aria-modal="true" onClick={() => setWtCloseAsk(false)}>
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))] sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-base font-extrabold text-slate-900">{t("wt_discard_title")}</h3>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setWtCloseAsk(false)} data-testid="wt-discard-cancel"
                className="min-h-[48px] flex-1 rounded-full bg-brand text-sm font-bold text-white active:scale-95">
                {t("wt_keep_recording")}
              </button>
              <button onClick={discardWalkthrough} data-testid="wt-discard-confirm"
                className="min-h-[48px] flex-1 rounded-full bg-red-600 text-sm font-bold text-white active:scale-95">
                {t("wt_discard_yes")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Round 18 #1: a failed Done shows an error with Retry — photos + audio
          stay queued on-device either way. */}
      {wtFinishError && createPortal(
        <div className="fixed inset-0 z-[96] flex items-end justify-center bg-black/60 sm:items-center" data-testid="wt-finish-error"
          role="alertdialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))] sm:rounded-3xl">
            <h3 className="font-heading text-base font-extrabold text-red-700">{t("wt_finish_failed")}</h3>
            <div className="mt-4 flex gap-2">
              <button onClick={retryWtFinish} data-testid="wt-finish-retry"
                className="min-h-[48px] flex-1 rounded-full bg-brand text-sm font-bold text-white active:scale-95">
                {t("wt_retry")}
              </button>
              <button onClick={() => { setWtFinishError(false); toast(t("wt_saved_offline")); wtResetForNewSession(); handleClose(true); }}
                data-testid="wt-finish-close"
                className="min-h-[48px] flex-1 rounded-full border border-slate-300 text-sm font-bold text-slate-600">
                {t("cam_close")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewIdx !== null && shots[previewIdx] && (
        <CapturePreview
          shots={shots}
          index={previewIdx}
          setIndex={setPreviewIdx}
          onClose={() => setPreviewIdx(null)}
          onDelete={(i) => { removeShot(i); setPreviewIdx(shots.length > 1 ? Math.max(0, i - 1) : null); }}
          onRetake={(i) => { removeShot(i); setPreviewIdx(null); }}
          onKeep={keepShot}
        />
      )}
    </>,
    document.body
  );
}
